import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConstants } from '@/contexts/ConstantsContext';
import {
  Inbox,
  UserPlus,
  RefreshCw,
  Phone,
  Building,
  MapPin,
  CheckSquare,
  Square
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnassignedLeads = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { users } = useConstants();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignToUser, setAssignToUser] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchUnassignedLeads();
  }, [isAdmin, navigate]);

  const fetchUnassignedLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads/unassigned`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load unassigned leads');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const handleAssignLeads = async () => {
    if (!assignToUser || selectedLeads.length === 0) {
      toast.error('Please select leads and a user');
      return;
    }

    setAssigning(true);
    try {
      await axios.post(`${API}/leads/bulk-reassign`, {
        lead_ids: selectedLeads,
        new_owner_id: assignToUser
      });
      toast.success(`Assigned ${selectedLeads.length} leads`);
      setSelectedLeads([]);
      setAssignDialogOpen(false);
      setAssignToUser('');
      fetchUnassignedLeads();
    } catch (error) {
      toast.error('Failed to assign leads');
    } finally {
      setAssigning(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-6" data-testid="unassigned-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Inbox className="w-8 h-8 text-muted-foreground" />
            Unassigned Leads
          </h1>
          <p className="text-muted-foreground mt-1">
            {leads.length} leads waiting to be assigned
          </p>
        </div>
        <div className="flex gap-2">
          {selectedLeads.length > 0 && (
            <Button onClick={() => setAssignDialogOpen(true)} data-testid="assign-selected-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Assign Selected ({selectedLeads.length})
            </Button>
          )}
          <Button variant="outline" onClick={fetchUnassignedLeads} className="border-border">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : leads.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Inbox className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold">No Unassigned Leads</h3>
            <p className="text-muted-foreground mt-2">All leads have been assigned to users.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Lead Queue</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleSelectAll}
                className="text-muted-foreground"
              >
                {selectedLeads.length === leads.length ? (
                  <><CheckSquare className="w-4 h-4 mr-2" /> Deselect All</>
                ) : (
                  <><Square className="w-4 h-4 mr-2" /> Select All</>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {leads.map(lead => (
              <div
                key={lead.id}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                  selectedLeads.includes(lead.id) 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/30'
                }`}
                onClick={() => toggleSelectLead(lead.id)}
                data-testid={`unassigned-lead-${lead.id}`}
              >
                <Checkbox 
                  checked={selectedLeads.includes(lead.id)}
                  onCheckedChange={() => toggleSelectLead(lead.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{lead.full_name}</span>
                    <Badge variant="outline" className={lead.country_market === 'UK' ? 'badge-uk' : 'badge-usa'}>
                      {lead.country_market}
                    </Badge>
                    {lead.local_time && (
                      <span className={`text-xs ${lead.outside_call_hours ? 'text-amber-400' : 'text-muted-foreground'}`}>
                        🕐 {lead.local_time} local
                        {lead.outside_call_hours && ' ⚠️'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {lead.company_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <span className="font-mono">{lead.phone}</span>
                    </span>
                    {lead.city_state && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {lead.city_state}
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant="outline">{lead.industry}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Assign {selectedLeads.length} Leads</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign To</label>
              <Select value={assignToUser} onValueChange={setAssignToUser}>
                <SelectTrigger className="bg-secondary border-border" data-testid="assign-user-select">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={handleAssignLeads} disabled={assigning || !assignToUser}>
              {assigning ? 'Assigning...' : 'Assign Leads'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnassignedLeads;
